import { cn } from "@/lib/cn";

import { Appear } from "@/components/landing/Appear";

const sunset1x = "/images/landing/solution/sunset-1x.webp";
const sunset2x = "/images/landing/solution/sunset-2x.webp";
const riskCard = "/images/landing/solution/risk-card.png";
const okxCircle = "/images/landing/solution/okx-circle.png";
const balanceCard = "/images/landing/solution/balance-card.png";

const NOISE_SVG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  1 0 0 0 0'/></filter><rect width='100%' height='100%' filter='url(#n)'/></svg>`
  );

interface CardProps {
  title: string;
  text: string;
  children: React.ReactNode;
  align: "end" | "center";
  delay: number;
}

const SolutionCard = ({ title, text, children, align, delay }: CardProps) => (
  <Appear
    delay={delay}
    className={cn(
      "relative isolate flex h-[22.9375rem] flex-1 flex-col gap-4 overflow-hidden rounded-2xl bg-[rgb(27_27_27/0.55)] px-2 pt-2 pb-6",
      // Потолок ширины: без него на 767px карточка раздувалась почти до 600px
      "max-lg:aspect-[341/367] max-lg:h-auto max-lg:w-[min(78%,21rem)] max-lg:flex-none max-lg:snap-start",
      "shadow-[0_4px_28px_0_rgb(0_0_0/0.04)]"
    )}
  >
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 -z-10 opacity-25"
      style={{ backgroundImage: `url("${NOISE_SVG}")` }}
    />

    <div
      className={cn(
        // min-h-0: иначе область держит высоту картинки и выдавливает текст
        "flex min-h-0 flex-1 items-center rounded-sm bg-[linear-gradient(180deg,rgb(0_0_0/0.42)_0%,rgb(0_0_0/0)_100%)]",
        align === "end" ? "justify-end" : "justify-center"
      )}
    >
      {children}
    </div>

    <div className="flex flex-col gap-4 px-2 max-md:gap-3">
      <h3 className="text-[1.125rem] leading-none font-bold tracking-[-0.01em] text-white">
        {title}
      </h3>
      <p className="text-[0.75rem] leading-[1.3] font-semibold text-white/55">{text}</p>
    </div>
  </Appear>
);

// Ниже 1024 пропорция фото не держится: секция стала бы ниже одной карточки
export const SolutionRowSection = () => (
  <section className="relative aspect-[1440/825] w-full overflow-hidden max-lg:aspect-auto">
    <img
      src={sunset1x}
      srcSet={`${sunset1x} 1440w, ${sunset2x} 2880w`}
      sizes="100vw"
      alt=""
      className="absolute inset-0 h-full w-full object-cover select-none max-lg:object-bottom"
    />

    <div className="landing-container scrollbar-hidden relative flex gap-2 pt-[11rem] max-lg:snap-x max-lg:snap-mandatory max-lg:gap-3 max-lg:overflow-x-auto max-lg:pt-14 max-lg:pr-4 max-lg:pb-[11rem]">
      <SolutionCard
        title="Выбираешь риск"
        text="Устанавливай комфортный уровень риска. Бот никогда не выйдет за его рамки."
        align="end"
        delay={0}
      >
        <img
          src={riskCard}
          alt="Виджет «Установить лимит»"
          className="max-h-full w-[74%] select-none"
        />
      </SolutionCard>

      <SolutionCard
        title="Подключаешь биржу"
        text="Безопасное соединение через API за 2 минуты без права вывода средств."
        align="center"
        delay={110}
      >
        <img
          src={okxCircle}
          alt="OKX"
          className="max-h-full w-[34%] select-none"
        />
      </SolutionCard>

      <SolutionCard
        title="Система работает сама"
        text="Бот круглосуточно анализирует рынок и совершает сделки по системе."
        align="end"
        delay={220}
      >
        <img
          src={balanceCard}
          alt="Баланс бота: $1201, +18%"
          className="max-h-full w-[73%] select-none"
        />
      </SolutionCard>
    </div>
  </section>
);
